import { Container } from "@mui/material";
import React from "react";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { getAdminBuildInfo } from "./buildInfo";
import { shouldAutoReloadChunkFailureOnce } from "./chunkLoadRecovery";
import {
  coercePublicErrorResponse,
  DEFAULT_PUBLIC_MESSAGE,
  formatPublicErrorForDisplay,
  sanitizePublicMessage,
} from "./errors/publicErrorContract";
import { generateCorrelationId } from "./logging/correlationId";
import { sanitizeAdminRouteForLogging } from "./logging/sanitizeAdminRouteForLogging";
import { reloadCurrentPage } from "./reloadCurrentPage";

import AdminLoadFailureNotice from "@/components/adminV2/shared/AdminLoadFailureNotice";
import { adminUiCopy } from "@/copy/adminUiCopy";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "utils/errorHandling",
);

const DEFAULT_ERROR_BOUNDARY_OPERATION = "UNCAUGHT_REACT_ERROR";
const DEFAULT_ERROR_BOUNDARY_FEATURE = "FND";
const ADMIN_ROUTE_CHUNK_LOAD_ERROR_CODE = "admin_route_chunk_load_failed";
const ADMIN_BOUNDARY_UNCLASSIFIED_ERROR_CODE =
  "admin_boundary_unclassified_error";
/**
 * A boundary event with no error code cannot be triaged: it arrives with no
 * bucket to group by and no name to search, which is how two Templates-page
 * failures on admin-production-126.1 (13 September 2026) reached their owner
 * as an unattributable pair. Every caught error now carries a code from this
 * closed set, so the class is always groupable. The set is finite and derived
 * only from the built-in error constructor name, so no message text, customer
 * content or identifier can reach the log through it.
 */
const ADMIN_BOUNDARY_ERROR_CODES_BY_NAME: Readonly<Record<string, string>> =
  Object.freeze({
    AggregateError: "admin_boundary_aggregate_error",
    EvalError: "admin_boundary_eval_error",
    RangeError: "admin_boundary_range_error",
    ReferenceError: "admin_boundary_reference_error",
    SyntaxError: "admin_boundary_syntax_error",
    TypeError: "admin_boundary_type_error",
    URIError: "admin_boundary_uri_error",
  });

/** Closed-class code for one caught boundary error, never free text. */
export const classifyAdminBoundaryErrorCode = (
  error: unknown,
  isChunkLoadError: boolean,
): string => {
  if (isChunkLoadError) return ADMIN_ROUTE_CHUNK_LOAD_ERROR_CODE;
  const name =
    error instanceof Error && typeof error.name === "string"
      ? error.name
      : undefined;
  return (
    (name &&
    Object.prototype.hasOwnProperty.call(
      ADMIN_BOUNDARY_ERROR_CODES_BY_NAME,
      name,
    )
      ? ADMIN_BOUNDARY_ERROR_CODES_BY_NAME[name]
      : undefined) ?? ADMIN_BOUNDARY_UNCLASSIFIED_ERROR_CODE
  );
};
const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Failed to load module script/i,
  /Loading chunk [^ ]+ failed/i,
  /ChunkLoadError/i,
];

const isErrorRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value && typeof value === "object");
};

const getNumberField = (
  record: Record<string, unknown>,
  field: string,
): number | undefined => {
  const value = record[field];
  return typeof value === "number" ? value : undefined;
};

const getStringField = (
  record: Record<string, unknown>,
  field: string,
): string | undefined => {
  const value = record[field];
  return typeof value === "string" ? value : undefined;
};

const getRecordField = (
  record: Record<string, unknown>,
  field: string,
): Record<string, unknown> | undefined => {
  const value = record[field];
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;
};

const buildPublicErrorInput = (
  errorRecord: Record<string, unknown>,
  fallbackMessage: string,
): {
  status?: number;
  data: unknown;
  correlationId: string | null;
} => {
  const responseRecord = getRecordField(errorRecord, "response");
  const responseStatus = responseRecord
    ? getNumberField(responseRecord, "status")
    : undefined;
  const responseData = responseRecord?.["data"];

  return {
    status: responseStatus ?? getNumberField(errorRecord, "status"),
    correlationId: getStringField(errorRecord, "correlationId") ?? null,
    data:
      responseData ??
      errorRecord["data"] ??
      errorRecord["message"] ??
      fallbackMessage,
  };
};

const getRawErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.message;
  }

  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  return undefined;
};

const extractDynamicImportUrl = (message: string): string | null => {
  const match = message.match(/https?:\/\/[^\s"')]+/i);
  return match ? match[0] : null;
};

const getChunkFilename = (url: string | null): string | null => {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split("/").filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : null;
  } catch {
    const sanitizedUrl = url.split(/[?#]/, 1)[0] ?? "";
    const segments = sanitizedUrl.split("/").filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : null;
  }
};

const getChunkLoadFailureDetails = (
  error: unknown,
): {
  isChunkLoadError: boolean;
  chunkUrl: string | null;
  chunkFilename: string | null;
} => {
  const message = getRawErrorMessage(error) ?? "";
  const isChunkLoadError = CHUNK_LOAD_ERROR_PATTERNS.some((pattern) =>
    pattern.test(message),
  );

  if (!isChunkLoadError) {
    return {
      isChunkLoadError: false,
      chunkUrl: null,
      chunkFilename: null,
    };
  }

  const chunkUrl = extractDynamicImportUrl(message);

  return {
    isChunkLoadError: true,
    chunkUrl,
    chunkFilename: getChunkFilename(chunkUrl),
  };
};

/**
 * Application-level error wrapper used to add stable codes and (optionally) HTTP status information.
 *
 * @remarks
 * Prefer throwing `AppError` at UI/service boundaries so error displays can remain user-friendly while logs
 * preserve the underlying failure details.
 */
export class AppError extends Error {
  static fromApiError(apiError: {
    status: number;
    data: unknown;
    message: string;
  }): AppError {
    return new AppError(
      apiError.message || "An API error occurred",
      "API_ERROR",
      apiError.status,
    );
  }

  static fromFirebaseError(firebaseError: unknown): AppError {
    // Map Firebase error codes to user-friendly messages
    const errorMessages: Record<string, string> = {
      "auth/user-not-found": "No account found with this email address",
      "auth/wrong-password": "Incorrect password",
      "auth/too-many-requests":
        "Too many failed attempts. Please try again later",
      "auth/network-request-failed":
        "Network error. Please check your connection",
    };

    const code =
      firebaseError &&
      typeof firebaseError === "object" &&
      "code" in firebaseError &&
      typeof (firebaseError as { code?: unknown }).code === "string"
        ? (firebaseError as { code: string }).code
        : undefined;

    const fallbackMessage = sanitizePublicMessage(
      getRawErrorMessage(firebaseError) ?? "Authentication error",
    );

    const message = (code ? errorMessages[code] : undefined) ?? fallbackMessage;

    return new AppError(
      message,
      "AUTH_ERROR",
      undefined,
      firebaseError instanceof Error ? firebaseError : undefined,
    );
  }

  constructor(
    message: string,
    readonly code: string,
    readonly statusCode?: number,
    readonly originalError?: Error,
  ) {
    super(message);
    this.name = "AppError";
  }
}

interface ErrorBoundaryProps {
  children?: React.ReactNode;
  componentName?: string;
  feature?: string;
  operation?: string;
  route?: string;
  /**
   * A caught error clears when this changes. The route boundary passes the
   * path: every admin route renders at the same tree position, so one
   * boundary instance outlives navigation and would otherwise keep a crashed
   * page's notice on the next page, even after Back (F-214).
   */
  resetKey?: string;
}

// Error boundary component
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  correlationId?: string | null;
  publicErrorCode?: string | null;
  isChunkLoadError?: boolean;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  private hasLoggedError = false;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      correlationId: null,
      publicErrorCode: null,
      isChunkLoadError: false,
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (this.hasLoggedError) {
      return;
    }

    this.hasLoggedError = true;
    const correlationId = generateCorrelationId();
    const chunkLoadFailure = getChunkLoadFailureDetails(error);
    const buildInfo = getAdminBuildInfo();
    const shouldReload =
      chunkLoadFailure.isChunkLoadError && shouldAutoReloadChunkFailureOnce();

    const boundaryLogger = logger
      .withComponent(this.props.componentName ?? "AppErrorBoundary")
      .withCorrelationId(correlationId);

    boundaryLogger.error(
      "Unhandled React error boundary caught an uncaught exception",
      error,
      {
        feature: this.props.feature ?? DEFAULT_ERROR_BOUNDARY_FEATURE,
        operation: this.props.operation ?? DEFAULT_ERROR_BOUNDARY_OPERATION,
        componentStack: errorInfo.componentStack,
        route:
          this.props.route ??
          (typeof window !== "undefined"
            ? sanitizeAdminRouteForLogging(window.location.pathname)
            : undefined),
        errorCode: classifyAdminBoundaryErrorCode(
          error,
          chunkLoadFailure.isChunkLoadError,
        ),
        chunkUrl: chunkLoadFailure.chunkUrl ?? undefined,
        chunkFilename: chunkLoadFailure.chunkFilename ?? undefined,
        recoveryAction: chunkLoadFailure.isChunkLoadError
          ? "reload_admin"
          : undefined,
        shouldReload: chunkLoadFailure.isChunkLoadError
          ? shouldReload
          : undefined,
        buildVersion: buildInfo.buildVersion,
        buildSha: buildInfo.buildSha ?? undefined,
      },
    );

    if (shouldReload) {
      reloadCurrentPage();
    }

    this.setState({
      correlationId,
      publicErrorCode: classifyAdminBoundaryErrorCode(
        error,
        chunkLoadFailure.isChunkLoadError,
      ),
      isChunkLoadError: chunkLoadFailure.isChunkLoadError,
    });
  }

  override componentDidUpdate(
    previous: ErrorBoundaryProps,
    previousState: ErrorBoundaryState,
  ) {
    // Only an error already shown before the key changed is cleared; a crash
    // on the new page itself (same commit) keeps its notice.
    if (
      previousState.hasError &&
      this.state.hasError &&
      previous.resetKey !== this.props.resetKey
    ) {
      this.hasLoggedError = false;
      this.setState({
        hasError: false,
        error: undefined,
        correlationId: null,
        publicErrorCode: null,
        isChunkLoadError: false,
      });
    }
  }

  override render() {
    if (this.state.hasError) {
      if (this.state.isChunkLoadError) {
        return React.createElement(
          Container,
          {},
          React.createElement(AdminLoadFailureNotice, {
            title: adminUiCopy.adminLoadFailure.chunkLoadTitle,
            message: adminUiCopy.adminLoadFailure.chunkLoadMessage,
            helpText: adminUiCopy.adminLoadFailure.chunkLoadHelp,
            error: {
              data: {
                errorCode: this.state.publicErrorCode,
                correlationId: this.state.correlationId,
              },
              correlationId: this.state.correlationId,
            },
            onRetry: () => {
              reloadCurrentPage();
            },
            retryLabel: adminUiCopy.adminLoadFailure.reloadAdmin,
          }),
        );
      }

      // A caught render exception is not an approved public-error response.
      // Do not expose arbitrary exception text, even when it looks harmless.
      return React.createElement(
        Container,
        {},
        React.createElement(AdminLoadFailureNotice, {
          title: "Something went wrong",
          message: DEFAULT_PUBLIC_MESSAGE,
          error: {
            data: {
              errorCode:
                this.state.publicErrorCode ??
                ADMIN_BOUNDARY_UNCLASSIFIED_ERROR_CODE,
              correlationId: this.state.correlationId,
            },
          },
          onRetry: reloadCurrentPage,
          retryLabel: adminUiCopy.adminLoadFailure.reloadAdmin,
        }),
      );
    }

    return this.props.children;
  }
}

// Utility functions for common error handling patterns
export const handleAsyncError = async <T>(
  operation: () => Promise<T>,
  errorContext: string,
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    const coercedError = toError(error);
    logger.error(`Error in ${errorContext}`, coercedError, {
      feature: "FND",
      operation: "ASYNC_ERROR",
      errorContext,
    });
    throw error instanceof AppError
      ? error
      : new AppError(
          `Failed to ${errorContext}`,
          "ASYNC_ERROR",
          undefined,
          coercedError,
        );
  }
};

/**
 * Converts unknown values into an `Error` for consistent logging + error handling.
 *
 * @remarks
 * Browser APIs sometimes throw `DOMException` (not always an `Error` instance) and some libraries throw
 * plain objects/strings. We normalise those into a real `Error` so our logger can safely capture
 * `{ name, message, stack }` without unsafe casts.
 */
export const toError = (error: unknown): Error | undefined => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  if (error && typeof error === "object") {
    const name =
      "name" in error && typeof error.name === "string"
        ? error.name
        : undefined;
    const message = getRawErrorMessage(error);

    if (message) {
      const coerced = new Error(message);
      if (name) {
        coerced.name = name;
      }
      return coerced;
    }
  }

  return undefined;
};

export const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  return "name" in error && error.name === "AbortError";
};

export const isNetworkError = (error: unknown): boolean => {
  const message = getRawErrorMessage(error);

  return (
    typeof message === "string" &&
    (message.includes("Network Error") ||
      message.includes("fetch") ||
      message.includes("ERR_NETWORK"))
  );
};

export const getErrorMessage = (
  error: unknown,
  fallbackMessage: string = DEFAULT_PUBLIC_MESSAGE,
): string => {
  const details = getPublicErrorDetails(error, fallbackMessage);
  return formatPublicErrorForDisplay({
    errorCode: details.errorCode,
    message: details.message,
    correlationId: details.correlationId ?? "unknown",
  });
};

const RUNTIME_FAULT_NAMES = new Set([
  "TypeError",
  "ReferenceError",
  "RangeError",
  "SyntaxError",
]);

/** A thrown JavaScript fault, or RTK Query's serialised copy of one. */
const isRuntimeFault = (error: unknown): boolean =>
  error instanceof Error
    ? RUNTIME_FAULT_NAMES.has(error.name)
    : isErrorRecord(error) &&
      typeof error.name === "string" &&
      RUNTIME_FAULT_NAMES.has(error.name) &&
      !("status" in error);

export const getPublicErrorDetails = (
  error: unknown,
  fallbackMessage: string = DEFAULT_PUBLIC_MESSAGE,
): {
  message: string;
  errorCode: string;
  correlationId: string | null;
} => {
  // A JavaScript runtime failure (such as reading a property of undefined)
  // is a code fault, not a message for staff: show the caller's safe copy.
  if (isRuntimeFault(error)) {
    return {
      message: sanitizePublicMessage(fallbackMessage),
      errorCode: "INTERNAL_ERROR",
      correlationId: null,
    };
  }
  if (error instanceof AppError) {
    return {
      message: sanitizePublicMessage(
        getRawErrorMessage(error) ?? DEFAULT_PUBLIC_MESSAGE,
      ),
      errorCode: error.code || "INTERNAL_ERROR",
      correlationId: null,
    };
  }

  if (isErrorRecord(error)) {
    const publicError = coercePublicErrorResponse(
      buildPublicErrorInput(error, fallbackMessage),
    );

    const resolvedMessage =
      publicError.message === DEFAULT_PUBLIC_MESSAGE
        ? sanitizePublicMessage(fallbackMessage)
        : publicError.message;

    return {
      message: resolvedMessage,
      errorCode: publicError.errorCode,
      correlationId:
        publicError.correlationId === "unknown"
          ? null
          : publicError.correlationId,
    };
  }

  if (error instanceof Error) {
    const safeMessage = sanitizePublicMessage(
      getRawErrorMessage(error) ?? DEFAULT_PUBLIC_MESSAGE,
    );
    return {
      message:
        safeMessage !== DEFAULT_PUBLIC_MESSAGE
          ? safeMessage
          : sanitizePublicMessage(fallbackMessage),
      errorCode: "INTERNAL_ERROR",
      correlationId: null,
    };
  }

  if (typeof error === "string") {
    const safeMessage = sanitizePublicMessage(error);
    return {
      message:
        safeMessage !== DEFAULT_PUBLIC_MESSAGE
          ? safeMessage
          : sanitizePublicMessage(fallbackMessage),
      errorCode: "INTERNAL_ERROR",
      correlationId: null,
    };
  }

  return {
    message: sanitizePublicMessage(fallbackMessage),
    errorCode: "INTERNAL_ERROR",
    correlationId: null,
  };
};
